import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Flex,
  Box,
  useTheme,
} from '@chakra-ui/react';
import { updateUser } from '../../endpoints/api';
import { ToastMessage } from '../../components/toast';
import useAuth from '../../hooks/useAuth';
import { TextG12, TextG14 } from '../typography';

interface HandleEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentHandle: string;
}

const HandleEditModal: React.FC<HandleEditModalProps> = ({ isOpen, onClose, currentHandle }) => {
  const [handle, setHandle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { reFetchUserData } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    if (isOpen) {
      setHandle(currentHandle);
      setError('');
    }
  }, [isOpen, currentHandle]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHandle(e.target.value);
    setError('');
  };

  const handleSubmit = async () => {
    if (!handle.trim()) {
      setError('Handle cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUser({ handle });
      await reFetchUserData();
      ToastMessage({
        status: 'success',
        text: 'Your handle has been updated successfully!',
      });
      onClose();
    } catch (e: any) {
      setError(e.message || 'An error occurred while updating your handle');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="12px" mx={4}>
        <ModalHeader>Edit Your Handle</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl>
            <Flex alignItems="center">
              <Box mr={1}>
                <TextG14>@</TextG14>
              </Box>
              <Input
                value={handle}
                onChange={handleChange}
                placeholder="Enter your handle"
                borderRadius="8px"
                autoFocus
              />
            </Flex>
            <TextG12 mt={2} color={theme.colors.grey}>
              This is how other users will see you in the community
            </TextG12>
            {error && (
              <Text mt={2} color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button 
            colorScheme="blue" 
            mr={3} 
            onClick={handleSubmit} 
            isLoading={isSubmitting}
            borderRadius="8px"
            background={theme.colors.blue}
          >
            Save
          </Button>
          <Button onClick={onClose} borderRadius="8px" variant="ghost">Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default HandleEditModal;